import { useCallback, useEffect, useState } from "react";
import {
  AssetRecordType,
  Box,
  TLAssetId,
  TLShapeId,
  createShapeId,
  useEditor,
  TLImageShape,
  TLShapePartial,
} from "@tldraw/tldraw";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const PdfJS = require("pdfjs-dist/legacy/build/pdf.min");
import UploadArea from "pages/creative/pageV2/UploadArea";
import CONFIG from "config/config";
import { getSignedUrl } from "graphQL/query/file";
import { usePDFUpload } from "pages/flyer/builder/drawer/contexts/PDFUploadContext";

// Set up the worker source
PdfJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PdfJS.version}/pdf.worker.min.js`;

interface PdfPage {
  src: string;
  bounds: Box;
  assetId: TLAssetId;
  shapeId: TLShapeId;
  s3Url?: string;
  actualWidth: number;
  actualHeight: number;
}

interface Pdf {
  name: string;
  pages: PdfPage[];
  source: ArrayBuffer;
}

const pageSpacing = 10;

// Helper function to convert base64 to blob and upload to S3 using signed URL
const uploadBase64ToS3 = async (base64Data: string, fileName: string): Promise<string> => {
  // Detect MIME type from the data URL prefix
  const mimeMatch = base64Data.match(/^data:(image\/[a-z]+);base64,/);
  const mimeType = mimeMatch ? mimeMatch[1] : "image/png";

  // Remove data URL prefix to get just the base64 data
  const base64WithoutPrefix = base64Data.replace(/^data:image\/[a-z]+;base64,/, "");

  // Convert base64 to blob
  const byteCharacters = atob(base64WithoutPrefix);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });

  // Create S3 key
  const key = `campaign-builder/uploads/${Date.now().toString()}/${fileName}`;

  // Get signed URL for upload
  const signedUrlPUT = await getSignedUrl(CONFIG.PDF_FLYER_BUCKET, key, "PUT", CONFIG.PDF_FLYER_BUCKET_REGION);

  // Upload to S3 using signed URL
  await fetch(signedUrlPUT, {
    method: "PUT",
    body: blob,
    headers: {
      "Content-Type": mimeType,
    },
  });

  // Get signed URL for reading, with S3 key marker for re-signing on expiry
  const signedUrlGET = await getSignedUrl(CONFIG.PDF_FLYER_BUCKET, key, "GET", CONFIG.PDF_FLYER_BUCKET_REGION);

  return `${signedUrlGET}#s3bucket=${encodeURIComponent(CONFIG.PDF_FLYER_BUCKET || "")}&s3key=${encodeURIComponent(
    key
  )}&s3region=${encodeURIComponent(CONFIG.PDF_FLYER_BUCKET_REGION || "")}`;
};

const usePdfImport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isImportingFromUrl, setIsImportingFromUrl] = useState(false);
  const editor = useEditor();
  const { startUpload, endUpload } = usePDFUpload();

  // Function to find the format shape and get its dimensions

  // Helper function to calculate fitted dimensions maintaining aspect ratio

  const importPdf = async (file: File) => {
    // Check if PDF shapes already exist for this file
    if (checkIfPdfShapesExist(file.name)) {
      console.log(`PDF shapes for "${file.name}" already exist, skipping import`);
      return;
    }

    setIsLoading(true);
    startUpload(); // Show global loader
    try {
      await importPdfFile(file, editor);
    } finally {
      setIsLoading(false);
      endUpload(); // Hide global loader
    }
  };

  const openPdfFileDialog = () => {
    const input = window.document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";
    input.addEventListener("change", async (e) => {
      const fileList = (e.target as HTMLInputElement).files;
      if (!fileList || fileList.length === 0) return;
      const file = fileList[0];
      await importPdf(file);
    });
    input.click();
  };
  // Function to download PDF from URL and create a File object
  const downloadPdfFromUrl = async (url: string, filename: string): Promise<File> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: "application/pdf" });
      return new File([blob], filename, { type: "application/pdf" });
    } catch (error) {
      console.error("Error downloading PDF:", error);
      throw error;
    }
  };

  // Helper function to check if PDF shapes already exist for a specific filename
  const checkIfPdfShapesExist = useCallback(
    (filename: string) => {
      const allShapes = editor.getSortedChildIdsForParent(editor.getCurrentPageId());
      const shapes = allShapes.map((id) => editor.getShape(id)).filter(Boolean);

      // Create the expected ID prefix for this PDF file
      const cleanFilename = filename.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9]/g, "_");
      const pdfShapePrefix = `pdf-${cleanFilename}-page-`;

      // Check if any shapes exist with IDs that match this PDF's pattern
      const pdfShapes = shapes.filter((shape) => shape?.id.startsWith(pdfShapePrefix));

      console.log(`Checking for existing PDF shapes with prefix: ${pdfShapePrefix}, found: ${pdfShapes.length}`);
      return pdfShapes.length > 0;
    },
    [editor]
  );

  const initPDFifNeeded = useCallback(() => {
    // Register custom external content handler for PDFs
    editor.registerExternalContentHandler("files", async ({ files }) => {
      // Filter for PDF files
      const pdfFiles = files.filter(
        (file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
      );

      if (pdfFiles.length > 0) {
        return importPdf(pdfFiles[0]);
      }

      // Let other files be handled normally
      return undefined;
    });

    const query = new URLSearchParams(window.location.search);
    const fileLink = query.get("fileLink");
    const hasHtmlContent = query.get("hasHtmlContent");

    if (fileLink && hasHtmlContent === "false" && !isImportingFromUrl) {
      const raw = fileLink.split("/").pop() || "document.pdf";
      const filename = raw.includes("?") ? raw.slice(0, raw.indexOf("?")) : raw;

      // Check if PDF shapes already exist for this specific file
      if (checkIfPdfShapesExist(filename)) {
        console.log(`PDF shapes for "${filename}" already exist, skipping import`);
        // Clean up URL parameters
        const url = new URL(window.location.href);
        url.searchParams.delete("fileLink");
        url.searchParams.delete("hasHtmlContent");
        window.history.replaceState({}, "", url.toString());
        return;
      }

      console.log(`Starting PDF import from URL for: ${filename}`);
      // Set flag to prevent concurrent imports
      setIsImportingFromUrl(true);

      // Download the PDF from the URL first
      downloadPdfFromUrl(fileLink, filename)
        .then(async (file) => {
          // Clear all existing shapes EXCEPT addressBox from the canvas before importing
          const allShapeIds = editor.getSortedChildIdsForParent(editor.getCurrentPageId());
          const shapeIdsToDelete = allShapeIds.filter(
            (id) => id !== ("shape:addressBox" as TLShapeId) // Preserve addressBox if it exists
          );
          if (shapeIdsToDelete.length > 0) {
            editor.deleteShapes(shapeIdsToDelete);
          }

          await importPdf(file);

          // Clean up URL parameters after successful import
          const url = new URL(window.location.href);
          url.searchParams.delete("fileLink");
          url.searchParams.delete("hasHtmlContent");
          window.history.replaceState({}, "", url.toString());
        })
        .catch((error) => {
          console.error("Failed to import PDF from URL:", error);
        })
        .finally(() => {
          setIsImportingFromUrl(false);
        });
    }
  }, [editor, isImportingFromUrl]);

  return {
    initPDFifNeeded,
    isLoading,
    importPdf,
    openPdfFileDialog,
  };
};

const PdfImport = () => {
  const { isLoading, importPdf } = usePdfImport();

  return (
    <div className="PdfPicker">
      <UploadArea onFileUpload={importPdf} isUploading={isLoading} localMode={true} />
    </div>
  );
};

export { usePdfImport, importPdfFile };
export default PdfImport;
const loadPdf = async (name: string, source: ArrayBuffer, editor: any): Promise<Pdf> => {
  // Create clean filename for ID generation
  const cleanFilename = name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9]/g, "_");
  const pdf = await PdfJS.getDocument(source.slice(0)).promise;
  const pages: PdfPage[] = [];

  const canvas = window.document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Failed to create canvas context");

  // Get format dimensions
  const formatDimensions = getFormatDimensions(editor.getSnapshot());
  // Target a high scale (4 → ~288 DPI) for print quality, but clamp per page so
  // the raster canvas never exceeds the browser/GPU max canvas size. Many GPUs
  // cap 2D-canvas backing stores at 4096px per side; drawing past that limit is
  // silently dropped, so a large page (e.g. a 2775×1875pt postcard at scale 4 =
  // 11100×7500) rendered only its top-left ~4096×4096 and the rest came out blank.
  const TARGET_SCALE = 4;
  const MAX_CANVAS_DIM = 4096;

  // Only process first 2 pages
  const maxPages = Math.min(2, pdf.numPages);

  let top = 0;
  let widest = 0;

  for (let i = 1; i <= maxPages; i++) {
    const page = await pdf.getPage(i);
    // Clamp the scale so neither canvas dimension exceeds MAX_CANVAS_DIM.
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(TARGET_SCALE, MAX_CANVAS_DIM / Math.max(base.width, base.height));
    const viewport = page.getViewport({ scale });
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const renderContext = {
      canvasContext: context,
      viewport,
    };
    await page.render(renderContext).promise;

    // Store actual PDF dimensions for proper aspect ratio scaling
    const actualWidth = viewport.width / scale;
    const actualHeight = viewport.height / scale;

    // Use format dimensions for layout positioning
    const width = formatDimensions.width;
    const height = formatDimensions.height;

    // Use high-quality JPEG (95%) for print export — visually indistinguishable
    // from lossless at 288 DPI, but ~10x smaller files than PNG
    const compressedSrc = canvas.toDataURL("image/jpeg", 0.95);

    // Create deterministic IDs based on filename and page number
    const assetId = AssetRecordType.createId(`pdf-${cleanFilename}-asset-${i}`);
    const shapeId = createShapeId(`pdf-${cleanFilename}-page-${i}`);

    pages.push({
      src: compressedSrc,
      bounds: new Box(0, top, width, height),
      assetId: assetId,
      shapeId: shapeId,
      actualWidth,
      actualHeight,
    });
    top += height + pageSpacing;
    widest = Math.max(widest, width);
  }
  canvas.width = 0;
  canvas.height = 0;

  for (const page of pages) {
    page.bounds.x = (widest - page.bounds.width) / 2;
  }

  return {
    name,
    pages,
    source,
  };
};

const importPdfFile = async (file: File, editor: any) => {
  const pdf = await loadPdf(file.name, await file.arrayBuffer(), editor);

  // Upload pages to S3 first
  const uploadPromises = pdf.pages.map(async (page, index) => {
    const fileName = `${file.name.replace(".pdf", "")}_page_${index + 1}.jpg`;
    const s3Url = await uploadBase64ToS3(page.src, fileName);
    return { ...page, s3Url };
  });

  const pagesWithS3Urls = await Promise.all(uploadPromises);
  const pdfWithS3Urls = { ...pdf, pages: pagesWithS3Urls };

  onOpenPdf(pdfWithS3Urls, editor);
};

const getFormatDimensions = (snapshot: any) => {
  const allShapes = Object.values(snapshot.document.store).filter((s: any) => s.type === "format");

  const frontShape = allShapes.find((s: any) => s.id === "shape:front");
  const backShape = allShapes.find((s: any) => s.id === "shape:back");

  if (frontShape && (frontShape as any).props) {
    return {
      width: (frontShape as any).props.w,
      height: (frontShape as any).props.h,
    };
  }

  // Fallback to default dimensions if no format shape is found
  return {
    width: 400,
    height: 600,
  };
};

const onOpenPdf = (pdf: Pdf, editor: any) => {
  const { frontShape, backShape } = getFrontAndBackShapes(editor.getSnapshot());

  // Check if assets already exist and only create new ones
  const existingAssetIds = new Set(editor.getAssets().map((asset: any) => asset.id));
  const newAssets = pdf.pages
    .filter((page) => !existingAssetIds.has(page.assetId))
    .map((page) => ({
      id: page.assetId,
      typeName: "asset",
      type: "image",
      meta: {},
      props: {
        w: page.actualWidth,
        h: page.actualHeight,
        mimeType: "image/jpeg",
        src: page.s3Url || page.src, // Use S3 URL if available, fallback to base64
        name: "page",
        isAnimated: false,
      },
    }));

  if (newAssets.length > 0) {
    console.log(`Creating ${newAssets.length} new assets`);
    editor.createAssets(newAssets);
  } else {
    console.log("All assets already exist, skipping asset creation");
  }

  // Create shapes for pages, placing first page on front and last page on back
  const shapesToCreate: TLShapePartial<TLImageShape>[] = [];

  // Get existing shape IDs to avoid duplicates
  const existingShapeIds = new Set(editor.getSortedChildIdsForParent(editor.getCurrentPageId()));

  pdf.pages.forEach((page, index) => {
    // Skip if shape already exists
    if (existingShapeIds.has(page.shapeId)) {
      console.log(`Shape ${page.shapeId} already exists, skipping`);
      return;
    }
    let shapeConfig: TLShapePartial<TLImageShape>;

    if (index === 0 && frontShape) {
      // First page goes on front shape - fit to shape while maintaining aspect ratio
      const { fittedWidth, fittedHeight } = calculateFittedDimensions(
        page.actualWidth,
        page.actualHeight,
        frontShape.props.w,
        frontShape.props.h
      );

      // Center the image within the shape
      const offsetX = (frontShape.props.w - fittedWidth) / 2;
      const offsetY = (frontShape.props.h - fittedHeight) / 2;

      shapeConfig = {
        id: page.shapeId,
        type: "image",
        x: frontShape.x + offsetX,
        y: frontShape.y + offsetY,
        isLocked: false,
        props: {
          assetId: page.assetId,
          w: fittedWidth,
          h: fittedHeight,
        },
      };
    } else if (index === pdf.pages.length - 1 && backShape) {
      // Last page goes on back shape - fit to shape while maintaining aspect ratio
      const { fittedWidth, fittedHeight } = calculateFittedDimensions(
        page.actualWidth,
        page.actualHeight,
        backShape.props.w,
        backShape.props.h
      );

      // Center the image within the shape
      const offsetX = (backShape.props.w - fittedWidth) / 2;
      const offsetY = (backShape.props.h - fittedHeight) / 2;

      shapeConfig = {
        id: page.shapeId,
        type: "image",
        x: backShape.x + offsetX,
        y: backShape.y + offsetY,
        isLocked: false,
        props: {
          assetId: page.assetId,
          w: fittedWidth,
          h: fittedHeight,
        },
      };
    } else {
      // Middle pages use original positioning
      shapeConfig = {
        id: page.shapeId,
        type: "image",
        x: page.bounds.x,
        y: page.bounds.y,
        isLocked: false,
        props: {
          assetId: page.assetId,
          w: page.bounds.w,
          h: page.bounds.h,
        },
      };
    }

    shapesToCreate.push(shapeConfig);
  });

  const ids = ["shape:frontBorderDashLine", "shape:backBorderDashLine", "shape:frontRedLine", "shape:backRedLine"];

  if (shapesToCreate.length > 0) {
    console.log(`Creating ${shapesToCreate.length} new shapes`);
    editor.run(
      () => {
        editor.createShapes(shapesToCreate);
        editor.bringToFront(ids as TLShapeId[]);
      },
      { ignoreShapeLock: true }
    );
  } else {
    console.log("All shapes already exist, skipping shape creation");
  }
};

// Function to get front and back shape positions and dimensions
const getFrontAndBackShapes = (snapshot: any) => {
  const allShapes = Object.values(snapshot.document.store).filter((s: any) => s.type === "format");

  const frontShape = allShapes.find((s: any) => s.id === "shape:front");
  const backShape = allShapes.find((s: any) => s.id === "shape:back");

  return {
    frontShape: frontShape ? (frontShape as any) : null,
    backShape: backShape ? (backShape as any) : null,
  };
};

const calculateFittedDimensions = (
  originalWidth: number,
  originalHeight: number,
  containerWidth: number,
  containerHeight: number
) => {
  const originalAspectRatio = originalWidth / originalHeight;
  const containerAspectRatio = containerWidth / containerHeight;

  let fittedWidth: number;
  let fittedHeight: number;

  if (originalAspectRatio > containerAspectRatio) {
    // Original is wider relative to height than container
    fittedWidth = containerWidth;
    fittedHeight = containerWidth / originalAspectRatio;
  } else {
    // Original is taller relative to width than container
    fittedHeight = containerHeight;
    fittedWidth = containerHeight * originalAspectRatio;
  }

  return { fittedWidth, fittedHeight };
};
